import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { roomTools } from "@memo-air/tools";
import { z } from "zod";

export const LIVE_DOCS_SERVER_NAME = "live_docs" as const;

const fileIdSchema = z.string().min(1).optional();

export const LIVE_DOCS_TOOLS_METADATA = [
  {
    name: "list_live_docs",
    description:
      "List the live docs, sheets, canvases, slides, browsers, desktops, calls and code runners in this conversation, newest first, with each one's id, kind, title and the tools that read it. Use it to find an object people mention by name before reading or editing it.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Listing live docs",
      done: "Listed live docs",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: "search_live_docs",
    description:
      "Search the text of this conversation's live docs and sheets, and the titles of its other live objects, for words or a phrase. Returns matching passages with the doc id they come from. Use it when people ask where something was written or what a doc says about a topic.",
    schema: {
      query: z
        .string()
        .min(1)
        .max(200)
        .describe("Words or a short phrase to find, e.g. 'renewal price'."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Searching live docs",
      done: "Searched live docs",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.create_live_doc,
    schema: {
      ...roomTools.create_live_doc.schema,
      file_id: fileIdSchema.describe(
        "Optional id of a file attached to this conversation (a PDF, Word doc, text or Markdown file). Its text becomes the doc's body after your markdown; pass an empty markdown to start from the file alone."
      ),
    },
    stake: "never_ask",
    displayLabels: { running: "Opening live doc", done: "Opened live doc" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.get_live_doc_outline,
    stake: "never_ask",
    displayLabels: {
      running: "Reading live doc outline",
      done: "Read live doc outline",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.edit_live_doc,
    stake: "never_ask",
    displayLabels: { running: "Editing live doc", done: "Edited live doc" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.comment_live_doc,
    stake: "never_ask",
    displayLabels: {
      running: "Commenting on live doc",
      done: "Commented on live doc",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.export_live_doc,
    stake: "never_ask",
    displayLabels: { running: "Exporting live doc", done: "Exported live doc" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.create_live_sheet,
    schema: {
      ...roomTools.create_live_sheet.schema,
      file_id: fileIdSchema.describe(
        'Optional id of a CSV or TSV file attached to this conversation. Its rows replace values, header row first; pass [[""]] as values when you give a file.'
      ),
    },
    stake: "never_ask",
    displayLabels: { running: "Opening live sheet", done: "Opened live sheet" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.read_live_sheet,
    stake: "never_ask",
    displayLabels: { running: "Reading live sheet", done: "Read live sheet" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.edit_live_sheet,
    stake: "never_ask",
    displayLabels: { running: "Editing live sheet", done: "Edited live sheet" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.export_live_sheet,
    stake: "never_ask",
    displayLabels: {
      running: "Exporting live sheet",
      done: "Exported live sheet",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.create_live_canvas,
    stake: "never_ask",
    displayLabels: {
      running: "Opening live canvas",
      done: "Opened live canvas",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.create_live_slides,
    stake: "never_ask",
    displayLabels: {
      running: "Opening live slides",
      done: "Opened live slides",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.read_live_canvas,
    stake: "never_ask",
    displayLabels: { running: "Reading live canvas", done: "Read live canvas" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.edit_live_canvas,
    stake: "never_ask",
    displayLabels: {
      running: "Editing live canvas",
      done: "Edited live canvas",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.export_live_slides,
    stake: "never_ask",
    displayLabels: {
      running: "Exporting live slides",
      done: "Exported live slides",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.open_live_browser,
    stake: "never_ask",
    displayLabels: {
      running: "Opening live browser",
      done: "Opened live browser",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.read_live_browser,
    stake: "never_ask",
    displayLabels: {
      running: "Reading live browser",
      done: "Read live browser",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.act_live_browser,
    stake: "low",
    displayLabels: {
      running: "Acting in live browser",
      done: "Acted in live browser",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.open_live_desktop,
    stake: "never_ask",
    displayLabels: {
      running: "Starting live desktop",
      done: "Started live desktop",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.read_live_desktop,
    stake: "never_ask",
    displayLabels: {
      running: "Looking at live desktop",
      done: "Looked at live desktop",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.act_live_desktop,
    stake: "low",
    displayLabels: {
      running: "Acting on live desktop",
      done: "Acted on live desktop",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.open_live_room,
    stake: "never_ask",
    displayLabels: { running: "Opening live room", done: "Opened live room" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.read_live_room,
    stake: "never_ask",
    displayLabels: {
      running: "Listening to live room",
      done: "Listened to live room",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    ...roomTools.speak_in_live_room,
    stake: "never_ask",
    displayLabels: {
      running: "Speaking in live room",
      done: "Spoke in live room",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: "run_code",
    description:
      "Run a Python 3 or Node script in a runner: a window in this conversation that shows the code, its output and a Re-run button, and can refresh itself on a schedule. The conversation's files are read-only in /files/conversation. Write results to the working directory: images, CSV, JSON, and an index.html, which becomes the runner's live dashboard and may load the other files by relative path. Python has pandas, numpy, matplotlib (save figures with savefig), requests and openpyxl. A run stops after 60 s and keeps at most 20 files or 32 MB. You get the status, exit code, the end of stdout and stderr, and the files written. To fix a script, call again with runner_id so the same runner re-runs instead of making another; to keep it current, call schedule_runner. Use it for a chart, table or dashboard people will want to see again or keep current, not for a one-off calculation whose result only goes into your reply.",
    schema: {
      title: z
        .string()
        .min(1)
        .max(120)
        .describe("What the runner shows, e.g. 'Weekly signups'."),
      language: z.enum(["python", "node"]),
      code: z.string().min(1).max(65_536).describe("The whole script."),
      runner_id: z
        .string()
        .min(1)
        .optional()
        .describe(
          "A runner's id from an earlier run_code result, to replace its script and re-run it. Leave out to make a new runner."
        ),
    },
    stake: "never_ask",
    displayLabels: { running: "Running code", done: "Ran code" },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: "schedule_runner",
    description:
      "Set a runner to re-run its script on its own every refresh_minutes (at least 15; 0 turns refresh off), so its chart or dashboard stays current without an agent turn. Refreshes run as the person you are working for and never overlap. Anyone who can edit the runner can pause it; three failed refreshes in a row pause it and say why in its window.",
    schema: {
      runner_id: z
        .string()
        .min(1)
        .describe("The runner's id from a run_code result."),
      refresh_minutes: z
        .number()
        .int()
        .min(0)
        .max(7 * 24 * 60)
        .describe("Minutes between refreshes: 0, or 15 and up."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Scheduling runner",
      done: "Scheduled runner",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const LIVE_DOCS_SERVER = {
  serverInfo: {
    name: LIVE_DOCS_SERVER_NAME,
    version: "1.0.0",
    description:
      "Create, read, edit and export live docs, sheets, canvases and slides that people and agents co-edit in real time.",
    authorization: null,
    icon: "ActionDocumentTextIcon",
    documentationUrl: null,
  },
  tools: LIVE_DOCS_TOOLS_METADATA,
} as const satisfies ServerMetadata;
